import React, { useState } from "react";
import axios from "axios";
import { Key, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal bloccante che forza il cambio password al primo accesso.
 * Non ha 'onClose' — si chiude solo dopo submit riuscito.
 */
export default function ForceChangePasswordModal({ onSuccess }) {
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const strong =
    pwd.length >= 8 &&
    /[a-z]/.test(pwd) &&
    /[A-Z0-9]/.test(pwd);

  const submit = async (e) => {
    e.preventDefault();
    if (!strong) {
      toast({ description: "La password deve avere almeno 8 caratteri con lettere e numeri o maiuscole.", variant: "destructive" });
      return;
    }
    if (pwd !== pwd2) {
      toast({ description: "Le due password non coincidono.", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/auth/change-password`, { new_password: pwd }, { withCredentials: true });
      toast({ description: "Password aggiornata con successo. Benvenuto!" });
      onSuccess();
    } catch (err) {
      toast({ description: "Errore: " + (err.response?.data?.detail || err.message), variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="force-change-password-modal"
    >
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-[#0a1f55] to-[#102a6b] text-white p-6 text-center">
          <div className="inline-flex h-12 w-12 rounded-full bg-[#f59e0b] text-[#0a1f55] items-center justify-center mb-3">
            <Key className="h-6 w-6" />
          </div>
          <div className="text-[10.5px] font-extrabold uppercase tracking-[0.22em] text-[#fbbf24]">Primo accesso</div>
          <h2 className="mt-1 text-xl font-extrabold">Imposta la tua password personale</h2>
          <p className="mt-2 text-[13px] text-slate-200 leading-relaxed">
            Per motivi di sicurezza, sostituisci ora la password iniziale con una scelta da te.
          </p>
        </div>

        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="block text-[11.5px] font-bold text-[#0a1f55] uppercase tracking-widest mb-1.5">
              Nuova password
            </label>
            <div className="relative">
              <Input
                type={show ? "text" : "password"}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                autoFocus
                required
                minLength={8}
                data-testid="new-password"
                placeholder="Min. 8 caratteri"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShow((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-700"
                tabIndex={-1}
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <ul className="mt-2 space-y-0.5 text-[11px]">
              <li className={pwd.length >= 8 ? "text-emerald-600" : "text-slate-500"}>
                • Almeno 8 caratteri
              </li>
              <li className={/[a-z]/.test(pwd) ? "text-emerald-600" : "text-slate-500"}>
                • Almeno una lettera minuscola
              </li>
              <li className={/[A-Z0-9]/.test(pwd) ? "text-emerald-600" : "text-slate-500"}>
                • Almeno una maiuscola o un numero
              </li>
            </ul>
          </div>

          <div>
            <label className="block text-[11.5px] font-bold text-[#0a1f55] uppercase tracking-widest mb-1.5">
              Ripeti password
            </label>
            <Input
              type={show ? "text" : "password"}
              value={pwd2}
              onChange={(e) => setPwd2(e.target.value)}
              required
              data-testid="new-password-confirm"
              placeholder="Conferma la nuova password"
            />
          </div>

          <Button
            type="submit"
            disabled={submitting || !strong || pwd !== pwd2}
            data-testid="submit-change-password"
            className="w-full h-11 bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-extrabold"
          >
            <ShieldCheck className="mr-2 h-4 w-4" />
            {submitting ? "Impostazione…" : "Imposta password e accedi"}
          </Button>
        </form>
      </div>
    </div>
  );
}
